/**
 * Filling a person's boxes from his Aadhaar card photo (owner, 2026-10-21):
 * name, S/O-D/O-W/O line, date of birth, age, gender and address. Only the
 * Aadhaar card gives details; a PAN card gives only its number (owner 10-21:
 * both cards are uploaded and they often differ).
 *
 * The template decides where a detail goes: a field may say `idPrefill`
 * (set by the admin in the Fields tab); without it the label decides
 * (`suggestIdDetail`), and a built-in location field its level.
 */
import { z } from 'zod';
import type { FieldDef, FieldType } from './ast';

/** What an Aadhaar card can give a box. Order = the editor's list order. */
export const ID_DETAILS = ['name', 'relation', 'relationName', 'dob', 'age', 'gender', 'address', 'door', 'street',
  'village', 'mandal', 'district', 'state', 'pincode'] as const;
export type IdDetail = (typeof ID_DETAILS)[number];
/** The field setting: one detail, or 'none' = never fill this box from a card. Absent = automatic. */
export const IdPrefill = z.enum([...ID_DETAILS, 'none'] as const);
export type IdPrefill = z.infer<typeof IdPrefill>;

export const ID_DETAIL_LABEL: Record<IdPrefill, string> = {
  name: 'Name', relation: 'Relation (S/o, D/o, W/o, C/o)', relationName: 'Father / husband name (the S/O, D/O, W/O line)',
  dob: 'Date of birth', age: 'Age (worked out from the date of birth)', gender: 'Gender', address: 'Full address',
  door: 'Door / house No.', street: 'Street / road', village: 'Village / town', mandal: 'Mandal / sub-district',
  district: 'District', state: 'State', pincode: 'PIN code', none: 'Never fill from the card',
};

/** The field types each detail can fill. */
export const ID_PREFILL_TYPES: Record<IdDetail, readonly FieldType[]> = {
  name: ['text'], relation: ['enum', 'text'], relationName: ['text'], dob: ['date', 'text'], age: ['number', 'text'],
  gender: ['enum', 'text'], address: ['text'], door: ['text'], street: ['text'], village: ['text'],
  mandal: ['text'], district: ['text'], state: ['text'], pincode: ['number', 'text'],
};

/** Types on which the editor shows the setting (a location field is filled by its level; it can only be switched off). */
export const ID_PREFILL_SHOWN: readonly FieldType[] = ['text', 'enum', 'date', 'number', 'location'];

export function idPrefillFits(type: FieldType, v: IdPrefill): boolean {
  if (v === 'none') return true;
  return ID_PREFILL_TYPES[v].includes(type);
}

const TYPE_WORD: Partial<Record<FieldType, string>> = { text: 'Text', enum: 'Choice', date: 'Date', number: 'Number' };

/** "a Date or Text field", for the error message. */
export function idPrefillFitHint(v: IdDetail): string {
  return `a ${ID_PREFILL_TYPES[v].map(t => TYPE_WORD[t] ?? t).join(' or ')} field`;
}

/** "S/o" → 'S', "Wife of" → 'W', "Smt." → null. */
export function relationOfOption(option: string): 'S' | 'D' | 'W' | 'C' | null {
  const o = option.trim().toLowerCase().replace(/\s+/g, ' ');
  const m = /^([sdwc])\s*\/\s*o\.?$/.exec(o);
  if (m) return m[1]!.toUpperCase() as 'S' | 'D' | 'W' | 'C';
  if (/^son of$/.test(o)) return 'S';
  if (/^daughter of$/.test(o)) return 'D';
  if (/^wife of$/.test(o)) return 'W';
  if (/^care of$/.test(o)) return 'C';
  return null;
}

/** "Male" / "M" / "పురుషుడు" → 'M', "Female" → 'F', "Transgender" → 'T', "Mr." → null. */
export function genderOfOption(option: string): 'M' | 'F' | 'T' | null {
  const o = option.trim().toLowerCase();
  if (/^(m|male|man|పురుషుడు|పురుషులు)$/.test(o)) return 'M';
  if (/^(f|female|woman|స్త్రీ|మహిళ)$/.test(o)) return 'F';
  if (/^(t|tg|transgender|third gender)$/.test(o)) return 'T';
  return null;
}

const SOMEONE_ELSE = /\b(minor|guardian|mother|spouse|brother|sister|child|children|nominee|witness|identifier|representative|officer|attorney|agent|partner|director|proprietor|signatory|authori[sz]ed|firm|company|bank|branch|office|layout|venture|apartment|complex|project|survey|plot|flat|property|schedule|boundary|society)\b/;
const PLACE_WORD = /\b(village|town|city|mandal|district|street|road|colony|state|locality|place)\b/;

/** The automatic choice from the label (first match wins), or null. */
export function suggestIdDetail(def: Pick<FieldDef, 'label' | 'type' | 'options'>): IdDetail | null {
  const t = def.type;
  if (!['text', 'enum', 'date', 'number'].includes(t)) return null;
  if (/[\u0C00-\u0C7F]/.test(def.label)) return null;
  const l = def.label.normalize('NFC').toLowerCase().replace(/[’']s\b/g, ' s');
  if (SOMEONE_ELSE.test(l) || /\b(son|daughter|wife)\b(?!\s+of\b)/.test(l)) return null;
  const opts = def.options ?? [];
  if (t === 'enum' && opts.length && opts.every(o => relationOfOption(o))) return 'relation';
  if (t === 'text' && /^\s*[sdwc]\s*\/\s*o\.?\s*$/.test(l)) return 'relationName';
  if (t === 'text' && /\b(father|husband|son of|daughter of|wife of|care of|relative)\b/.test(l)) return 'relationName';
  if (t === 'text' && /\bname\b/.test(l) && !PLACE_WORD.test(l)) return 'name';
  if ((t === 'date' || t === 'text') && /(\bdate of birth\b|\bbirth date\b|\bdob\b|\bd\.o\.b\b|\bborn\b)/.test(l)) return 'dob';
  if ((t === 'number' || t === 'text') && /\bage\b/.test(l)) return 'age';
  if (/\b(gender|sex)\b/.test(l)) {
    if (t === 'text') return 'gender';
    if (t === 'enum' && opts.filter(o => genderOfOption(o)).length >= 2) return 'gender';
  }
  if ((t === 'number' || t === 'text') && /\b(pin ?code|pin|postal code)\b/.test(l)) return 'pincode';
  if (t !== 'text') return null;
  if (/\b(door|house|h|d)\s*\.?\s*(no|number)\b/.test(l)) return 'door';
  if (/\b(street|road|lane)\b/.test(l)) return 'street';
  if (/\b(village|town|city|vtc)\b/.test(l)) return 'village';
  if (/\b(mandal|tehsil|taluka?|sub[- ]?district)\b/.test(l)) return 'mandal';
  if (/\b(district|dist)\b/.test(l)) return 'district';
  if (/\bstate\b/.test(l)) return 'state';
  if (/\b(address|residing|resident|residence|r\/o)\b/.test(l)) return 'address';
  return null;
}

/** The detail a field takes from an Aadhaar card: its setting, else the label's suggestion (null = none). */
export function idDetailOf(def: Pick<FieldDef, 'label' | 'type' | 'options' | 'idPrefill'>): IdDetail | null {
  if (def.idPrefill === 'none') return null;
  if (def.idPrefill && idPrefillFits(def.type, def.idPrefill)) return def.idPrefill;
  return suggestIdDetail(def);
}
